import { useTranslation } from 'react-i18next';
import PageHeader from '@casops/components/layout/page-header/PageHeader';
import { ModelName, QueryTag, TagList } from '@casops/store/api';
import StandardPage from '@casops/templates/standard-page/StandardPage';
import NavTabs from '@cas/components/tabs/nav-tabs/NavTabs';
import { Outlet } from 'react-router-dom';
import { useMemo } from 'react';
import { NavTabModel } from '@cas/components/tabs/nav-tab/NavTab';
import { SidePanel } from '@casops/store/slices/globalSlice';
import { Permission } from '@casops/permissions';
import CreateItemButton from '@casops/components/inputs/create-item-button/CreateItemButton';
import { useGetPersonnelSafetySettingsQuery } from '@casops/store/apis/settingsApi';
import { SafetyAlertType } from '@casops/store/apis/commonModels';
import { useLivePersonnelSafetyNotificationsApi } from '@casops/store/apis/useLivePersonnelSafetyNotificationsApi';

const tagsToClearOnRefresh = [
  { type: QueryTag.SafetyAlert, id: TagList },
  QueryTag.SettingsPersonnelSafety,
];

const modelName = ModelName.SafetyAlertDuress;
const sidePanel = SidePanel.ManagePersonnelSafety;
const permission = Permission.SafetyManagement;

export function PersonnelSafetyPage() {
  const { t } = useTranslation();

  const { data: settings } = useGetPersonnelSafetySettingsQuery();

  const tracked = useMemo<Record<SafetyAlertType, boolean | undefined>>(() => {
    return {
      [SafetyAlertType.Duress]: settings?.trackDuress,
      [SafetyAlertType.ManDown]: settings?.trackManDown,
    };
  }, [settings?.trackDuress, settings?.trackManDown]);

  const notifications = useLivePersonnelSafetyNotificationsApi();

  const hasDuressAlerts = Boolean(tracked.Duress && notifications && notifications?.ds);
  const hasManDownAlerts = Boolean(tracked.ManDown && notifications && notifications?.md);

  const tabs = useMemo<NavTabModel[]>(
    () => [
      {
        title: t('Navigation.Safety.Duress'),
        to: '/assets/safety/duress',
        isDotted: hasDuressAlerts,
      },
      {
        title: t('Navigation.Safety.ManDown'),
        to: '/assets/safety/man-down',
        isDotted: hasManDownAlerts,
      },
    ],
    [t, hasDuressAlerts, hasManDownAlerts],
  );

  return (
    <StandardPage
      header={
        <PageHeader
          title={t('Navigation.Safety.General')}
          tagsToClearOnRefresh={tagsToClearOnRefresh}
        >
          <CreateItemButton
            modelName={modelName}
            sidePanel={sidePanel}
            permission={permission}
            label={t('Settings')}
            icon="system"
            variant="secondary"
          />
        </PageHeader>
      }
    >
      <NavTabs tabs={tabs} hasBorder />
      <Outlet context={tracked} />
    </StandardPage>
  );
}

export default PersonnelSafetyPage;
